import { useState, useCallback } from "react";
import { stripRoadPrefix } from "@/lib/geoText";

interface ReverseGeocodeResult {
  city: string;
  region: string;
  address: string;
  postalCode: string;
  displayName: string;
}

interface NominatimReverseResponse {
  display_name: string;
  address?: {
    road?: string;
    house_number?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    state?: string;
    postcode?: string;
  };
}

// Normalizuje nazwę województwa (usuwa "województwo" i zamienia na małe litery)
const normalizeRegion = (state: string | undefined): string => {
  if (!state) return "";
  
  const match = state.match(/województwo\s+(.+)/i);
  if (match) {
    return match[1].toLowerCase();
  }
  
  return state.toLowerCase().replace("województwo", "").trim();
};

// Formatowanie nazwy z wielkich liter
const capitalizeWords = (str: string): string => {
  return str
    .split(" ")
    .map((word) => {
      if (word.length === 0) return word;
      const smallWords = ["i", "w", "na", "do", "z", "od", "po", "przy", "za", "dla"];
      if (smallWords.includes(word.toLowerCase()) && word !== str.split(" ")[0]) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};

export const useReverseGeocode = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reverseGeocode = useCallback(async (
    lat: number,
    lng: number
  ): Promise<ReverseGeocodeResult | null> => {
    setIsLoading(true);
    setError(null);

    try {
      // Validate coordinates are in Poland
      if (lat < 49 || lat > 55 || lng < 14 || lng > 24) {
        throw new Error("Lokalizacja poza granicami Polski");
      }

      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?` +
          new URLSearchParams({
            lat: lat.toString(),
            lon: lng.toString(),
            format: "json",
            addressdetails: "1",
            zoom: "18", // Wysoki zoom = dokładniejsze dane adresowe
          }),
        {
          headers: {
            "User-Agent": "GdzieSchronisko-ShelterApp/1.0",
            "Accept-Language": "pl",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Błąd połączenia z serwisem geokodowania");
      }

      const data: NominatimReverseResponse = await response.json();

      if (!data || !data.address) {
        throw new Error("Nie znaleziono adresu dla tej lokalizacji");
      }

      const addr = data.address;
      const cityRaw = addr.city || addr.town || addr.village || addr.municipality || "";
      const city = capitalizeWords(cityRaw);
      const region = normalizeRegion(addr.state);
      const postalCode = addr.postcode || "";

      let address = "";
      if (addr.road) {
        address = capitalizeWords(stripRoadPrefix(addr.road));
        if (addr.house_number) {
          address += ` ${addr.house_number}`;
        }
      }

      return {
        city,
        region,
        address,
        postalCode,
        displayName: data.display_name,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Błąd geokodowania";
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    reverseGeocode,
    isLoading,
    error,
  };
};
